(function () {
  'use strict';

  const template = document.getElementById('TiltUpcartTermsTemplate');
  if (!template || window.__tiltUpcartTermsInitialized) return;
  window.__tiltUpcartTermsInitialized = true;

  // Public Upcart selectors: standard checkout and V1/V2 express containers.
  const checkoutSelector = '.upcart-checkout-button';
  const expressSelector = '#upcart-additional-checkout-buttons, #upcart-express-payments, .upcart-public-express-payments__container';
  const roots = new WeakSet();
  const checkoutAria = new WeakMap();
  const managedExpress = new WeakSet();
  let accepted = false;
  let showWarning = false;
  let cartToken = null;
  let connectedRoot = null;
  let observedContainer = null;
  let cartObserver = null;
  let discoveryObserver = null;
  let discoveryTimeout = null;
  let subscribed = false;
  let scheduled = false;
  let nextId = 0;

  function readCart(cart) {
    if (!cart || !Array.isArray(cart.items)) return;
    if (cart.items.length === 0 || (cartToken && cart.token && cart.token !== cartToken)) {
      accepted = false;
      showWarning = false;
    }
    if (cart.token) cartToken = cart.token;
  }

  function getRoot() {
    return window.upcartDocumentOrShadowRoot || document;
  }

  function sync() {
    const root = getRoot();
    const cart = root.querySelector('.upcart-cart');
    if (!cart) return;
    if (typeof window.upcartGetCart === 'function') readCart(window.upcartGetCart());

    const buttons = cart.querySelectorAll(checkoutSelector);
    buttons.forEach(button => {
      const container = button.closest('.upcart-checkout-button-container') || button;
      let row = container.previousElementSibling;
      if (!row || !row.hasAttribute('data-tilt-upcart-terms')) {
        row = template.content.firstElementChild.cloneNode(true);
        const id = 'TiltUpcartTerms-' + (++nextId);
        row.querySelector('input').id = id;
        row.querySelector('label').htmlFor = id;
        row.querySelector('p').id = id + '-error';
        row.querySelector('input').setAttribute('aria-describedby', id + '-error');
        container.before(row);
      }
      const checkbox = row.querySelector('input');
      checkbox.checked = accepted;
      checkbox.setAttribute('aria-invalid', String(showWarning && !accepted));
      row.querySelector('p').hidden = !showWarning || accepted;
      row.classList.toggle('is-error', showWarning && !accepted);

      if (!accepted) {
        if (!checkoutAria.has(button)) checkoutAria.set(button, button.getAttribute('aria-disabled'));
        button.setAttribute('aria-disabled', 'true');
      } else if (checkoutAria.has(button)) {
        const original = checkoutAria.get(button);
        if (original === null) button.removeAttribute('aria-disabled');
        else button.setAttribute('aria-disabled', original);
        checkoutAria.delete(button);
      }
    });

    // Gate only the outer container. Never replace, clone or intercept wallet buttons.
    // Browsers without native inert keep express payments operational.
    if (buttons.length && 'inert' in HTMLElement.prototype) {
      cart.querySelectorAll(expressSelector).forEach(container => {
        if (!accepted && !container.inert) {
          container.inert = true;
          managedExpress.add(container);
        } else if (accepted && managedExpress.has(container)) {
          container.inert = false;
          managedExpress.delete(container);
        }
      });
    }
  }

  function schedule() {
    if (scheduled) return;
    scheduled = true;
    queueMicrotask(function () {
      scheduled = false;
      connect();
      sync();
    });
  }

  function warn() {
    showWarning = true;
    sync();
    const row = getRoot().querySelector('[data-tilt-upcart-terms]');
    if (row) {
      // Re-run the short shake on every blocked click, so the user sees where the problem is.
      row.classList.remove('is-shake');
      void row.offsetWidth;
      row.classList.add('is-shake');
      row.querySelector('input').focus({ preventScroll: true });
      row.scrollIntoView({ block: 'nearest' });
    }
  }

  function guard(event) {
    if (accepted) return;
    const button = event.composedPath().find(node => node instanceof Element && node.matches(checkoutSelector));
    if (!button) return;
    // Capture before Upcart's React checkout handler can navigate.
    event.preventDefault();
    event.stopImmediatePropagation();
    connect();
    warn();
  }

  function change(event) {
    const checkbox = event.composedPath().find(node => node instanceof Element && node.matches('.tilt-upcart-terms__checkbox'));
    if (!checkbox) return;
    accepted = checkbox.checked;
    showWarning = false;
    sync();
  }

  function bindRoot(root) {
    if (roots.has(root)) return;
    roots.add(root);
    root.addEventListener('click', guard, true);
    root.addEventListener('auxclick', guard, true);
    root.addEventListener('change', change);
  }

  function cartEvent(event) {
    readCart(event && event.cart);
    schedule();
  }

  function stopDiscovery() {
    if (discoveryObserver) discoveryObserver.disconnect();
    discoveryObserver = null;
    clearTimeout(discoveryTimeout);
  }

  function connect() {
    const root = getRoot();
    const cart = root.querySelector('.upcart-cart');
    if (!cart) return;
    bindRoot(root);
    if (!subscribed && typeof window.upcartSubscribeCartLoaded === 'function') {
      subscribed = true;
      window.upcartSubscribeCartLoaded(cartEvent);
      window.upcartSubscribeCartUpdated(cartEvent);
      window.upcartSubscribeCartOpened(cartEvent);
    }

    // After discovery, observe only Upcart's own container, not the whole storefront.
    const container = root === document ? (document.getElementById('upCart') || cart.parentNode) : root;
    if (connectedRoot !== root || observedContainer !== container) {
      if (cartObserver) cartObserver.disconnect();
      cartObserver = new MutationObserver(schedule);
      cartObserver.observe(container, { childList: true, subtree: true });
      connectedRoot = root;
      observedContainer = container;
    }
    stopDiscovery();
  }

  function initialize() {
    connect();
    sync();
    if (!connectedRoot && !discoveryObserver && document.body) {
      discoveryObserver = new MutationObserver(schedule);
      discoveryObserver.observe(document.body, { childList: true, subtree: true });
      discoveryTimeout = setTimeout(stopDiscovery, 20000);
    }
  }

  bindRoot(document);
  // Also catch the first load when Upcart initializes after the theme script.
  // This is the public event underlying upcartSubscribeCartLoaded, and crosses
  // the shadow boundary even when a body MutationObserver cannot see the cart.
  window.addEventListener('aftersell-upcart:public-events:cart-loaded', function (event) {
    cartEvent(event.detail);
  }, { once: true });
  window.addEventListener('load', initialize, { once: true });
  window.addEventListener('pageshow', function (event) {
    if (event.persisted) {
      accepted = false;
      showWarning = false;
      schedule();
    }
  });
  initialize();
})();
